import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { useT } from '../i18n';
import ConfirmDialog from './ConfirmDialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import ListInput from './ListInput';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Separator } from './ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Textarea } from './ui/textarea';
import {
  Shield,
  AlertTriangle,
  Ban,
  CheckCircle,
  Activity,
  Plus,
  Edit,
  Trash2,
  Eye,
  RefreshCw,
  Filter,
  Globe,
  Smartphone,
  Clock,
  UserCheck,
  UserX
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import XpnsrPageHeader from './XpnsrPageHeader';
import SettingWarning from './SettingWarning';
import { useSelfHosted } from '../lib/instanceLicence';

export default function AntiFraud() {
  const { user } = useAuth();
  const { t } = useT();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('filters');
  const [deleteFilterTarget, setDeleteFilterTarget] = useState(null);

  // State for data
  const [fraudFilters, setFraudFilters] = useState([]);
  const [blacklistedIPs, setBlacklistedIPs] = useState([]);
  const [whitelistedIPs, setWhitelistedIPs] = useState([]);
  const [suspiciousActivities, setSuspiciousActivities] = useState([]);

  // Dialog states
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [ipDialogOpen, setIpDialogOpen] = useState(false);
  const [ipDialogType, setIpDialogType] = useState('blacklist'); // blacklist or whitelist
  const [editingItem, setEditingItem] = useState(null);

  // Wave 25d — bot-detection blocked-samples drill-down state.
  const [samplesDialogOpen, setSamplesDialogOpen] = useState(false);
  const [samplesFilter, setSamplesFilter] = useState(null);
  const [samplesLoading, setSamplesLoading] = useState(false);
  const [samplesData, setSamplesData] = useState([]);

  // Form states
  const [filterForm, setFilterForm] = useState({
    name: '',
    description: '',
    filter_type: 'bot_detection',
    conditions: {},
    action: 'block',
    redirect_url: ''
  });

  const [ipForm, setIpForm] = useState({
    ip_address: '',
    reason: '',
    notes: ''
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  // Which paid IP-risk providers have a key on this server (never the key itself).
  const selfHosted = useSelfHosted();
  const [providerKeys, setProviderKeys] = useState(null);
  useEffect(() => {
    axios
      .get(`${API}/fraud-filters/provider-keys`)
      .then((r) => setProviderKeys(r?.data || null))
      .catch(() => setProviderKeys(null));
  }, []);
  const providerMissing = (provider) => Boolean(providerKeys) && !providerKeys[provider || 'ipqs'];
  const envHint = selfHosted ? ` ${t('Set IPQS_API_KEY or FRAUDSCORE_API_KEY in the backend environment.')}` : '';

  const fetchAllData = async () => {
    try {
      setLoading(true);

      const [filtersRes, blacklistRes, whitelistRes, activitiesRes] = await Promise.allSettled([
        axios.get(`${API}/fraud-filters`),
        axios.get(`${API}/blacklisted-ips`),
        axios.get(`${API}/whitelisted-ips`),
        axios.get(`${API}/suspicious-activities?limit=50`),
      ]);
      if (filtersRes.status === 'fulfilled') setFraudFilters(filtersRes.value.data);
      if (blacklistRes.status === 'fulfilled') setBlacklistedIPs(blacklistRes.value.data);
      if (whitelistRes.status === 'fulfilled') setWhitelistedIPs(whitelistRes.value.data);
      if (activitiesRes.status === 'fulfilled') setSuspiciousActivities(activitiesRes.value.data);

    } catch (error) {
      console.error('Error fetching anti-fraud data:', error);
      toast.error(t('Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFilter = async () => {
    try {
      let conditions = {};

      // Build conditions based on filter type
      if (filterForm.filter_type === 'click_frequency') {
        conditions = {
          max_clicks_per_minute: parseInt(filterForm.conditions.max_clicks_per_minute) || 10
        };
      } else if (filterForm.filter_type === 'country_block') {
        conditions = {
          blocked_countries: filterForm.conditions.blocked_countries || []
        };
      } else if (filterForm.filter_type === 'asn_block') {
        // Anti-Fraud Tier 2: block by ASN number(s) and / or label substring(s).
        const asns = (filterForm.conditions.asns || [])
          .map((x) => parseInt(x, 10))
          .filter((x) => !Number.isNaN(x));
        conditions = {
          asns,
          asn_names: filterForm.conditions.asn_names || []
        };
      } else if (filterForm.filter_type === 'datacenter_block') {
        // Optional whitelist of ASN numbers / label substrings to allow
        // through (e.g. the operator's own AWS gateway).
        conditions = {
          allow_asns: filterForm.conditions.allow_asns || []
        };
      } else if (filterForm.filter_type === 'proxy_vpn_block') {
        conditions = {
          check_tor: filterForm.conditions.check_tor !== false,
          check_proxy: !!filterForm.conditions.check_proxy,
          check_vpn: !!filterForm.conditions.check_vpn,
          provider: filterForm.conditions.provider || 'ipqs'
        };
      } else if (filterForm.filter_type === 'external_score') {
        conditions = {
          provider: filterForm.conditions.provider || 'ipqs',
          threshold: parseInt(filterForm.conditions.threshold, 10) || 85
        };
      } else if (filterForm.filter_type === 'tls_fingerprint') {
        // Wave 30 (audit-handoff §B.3) — TLS fingerprint anti-bot.
        // Operators tune three knobs: explicit blocked-fingerprint
        // list, classification policy (block all "bad" hashes), and
        // a blended risk-score threshold. The default policy is
        // unknown → allow + log; flip block_unknown only after the
        // workspace has tagged its corporate-proxy hashes.
        const blocked = (filterForm.conditions.blocked_fingerprints || '')
          .split(/[\s,]+/)
          .map((h) => h.trim())
          .filter(Boolean);
        const threshold = parseFloat(filterForm.conditions.risk_threshold);
        conditions = {
          blocked_fingerprints: blocked,
          block_classification: filterForm.conditions.block_classification || 'bad',
          block_unknown: !!filterForm.conditions.block_unknown,
          risk_threshold: Number.isFinite(threshold) ? threshold : 0.7
        };
      }

      const filterData = {
        ...filterForm,
        conditions
      };

      const response = await axios.post(`${API}/fraud-filters`, filterData);
      setFraudFilters([...fraudFilters, response.data]);
      setFilterDialogOpen(false);
      resetFilterForm();
      toast.success(t('Filter created'));
    } catch (error) {
      console.error('Error creating filter:', error);
      toast.error(t('Failed to create filter'));
    }
  };

  const handleUpdateFilter = async () => {
    if (!editingItem) return;

    try {
      const response = await axios.put(`${API}/fraud-filters/${editingItem.id}`, filterForm);
      setFraudFilters(fraudFilters.map(filter => filter.id === editingItem.id ? response.data : filter));
      setFilterDialogOpen(false);
      setEditingItem(null);
      resetFilterForm();
      toast.success(t('Filter updated'));
    } catch (error) {
      console.error('Error updating filter:', error);
      toast.error(t('Failed to update filter'));
    }
  };

  const handleDeleteFilter = async (filterId) => {
    try {
      await axios.delete(`${API}/fraud-filters/${filterId}`);
      setFraudFilters(fraudFilters.filter(filter => filter.id !== filterId));
      toast.success(t('Filter deleted'));
    } catch (error) {
      console.error('Error deleting filter:', error);
      toast.error(t('Failed to delete filter'));
    }
  };

  const handleAddIP = async () => {
    try {
      const endpoint = ipDialogType === 'blacklist' ? 'blacklisted-ips' : 'whitelisted-ips';
      const response = await axios.post(`${API}/${endpoint}`, ipForm);

      if (ipDialogType === 'blacklist') {
        setBlacklistedIPs([...blacklistedIPs, response.data]);
      } else {
        setWhitelistedIPs([...whitelistedIPs, response.data]);
      }

      setIpDialogOpen(false);
      resetIpForm();
      toast.success(ipDialogType === 'blacklist' ? t('IP added to blacklist') : t('IP added to whitelist'));
    } catch (error) {
      console.error('Error adding IP:', error);
      toast.error(error.response?.data?.detail || t('Failed to add IP'));
    }
  };

  const handleRemoveIP = async (ipId, type) => {
    try {
      const endpoint = type === 'blacklist' ? 'blacklisted-ips' : 'whitelisted-ips';
      await axios.delete(`${API}/${endpoint}/${ipId}`);

      if (type === 'blacklist') {
        setBlacklistedIPs(blacklistedIPs.filter(ip => ip.id !== ipId));
      } else {
        setWhitelistedIPs(whitelistedIPs.filter(ip => ip.id !== ipId));
      }

      toast.success(type === 'blacklist' ? t('IP removed from blacklist') : t('IP removed from whitelist'));
    } catch (error) {
      console.error('Error removing IP:', error);
      toast.error(t('Failed to remove IP'));
    }
  };

  const handleResolveActivity = async (activityId) => {
    try {
      await axios.put(`${API}/suspicious-activities/${activityId}/resolve`);
      setSuspiciousActivities(suspiciousActivities.map(activity =>
        activity.id === activityId ? { ...activity, is_resolved: true } : activity
      ));
      toast.success(t('Activity marked as resolved'));
    } catch (error) {
      console.error('Error resolving activity:', error);
      toast.error(t('Failed to update activity'));
    }
  };

  const resetFilterForm = () => {
    setFilterForm({
      name: '',
      description: '',
      filter_type: 'bot_detection',
      conditions: {},
      action: 'block',
      redirect_url: ''
    });
  };

  const resetIpForm = () => {
    setIpForm({
      ip_address: '',
      reason: '',
      notes: ''
    });
  };

  const openEditFilter = (filter) => {
    setEditingItem(filter);
    setFilterForm({
      name: filter.name,
      description: filter.description || '',
      filter_type: filter.filter_type,
      conditions: filter.conditions || {},
      action: filter.action,
      redirect_url: filter.redirect_url || ''
    });
    setFilterDialogOpen(true);
  };

  const openAddIP = (type) => {
    setIpDialogType(type);
    resetIpForm();
    setIpDialogOpen(true);
  };

  const openBlockedSamples = async (filter) => {
    setSamplesFilter(filter);
    setSamplesData([]);
    setSamplesDialogOpen(true);
    setSamplesLoading(true);
    try {
      const response = await axios.get(`${API}/fraud-filters/${filter.id}/blocked-samples`);
      setSamplesData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching blocked samples:', error);
      toast.error(t('Failed to load blocked samples'));
    } finally {
      setSamplesLoading(false);
    }
  };

  const getSeverityVariant = (severity) => {
    switch (severity) {
      case 'low': return 'secondary';
      case 'medium': return 'secondary';
      case 'high': return 'destructive';
      case 'critical': return 'destructive';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton h-8 w-48 mb-6"></div>
        <div className="skeleton h-96 w-full"></div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      <XpnsrPageHeader
        kicker={t('Defense')}
        title={t('Anti-fraud system')}
        subtitle={t('Filter junk traffic, block malicious IPs and review suspicious activity in one place.')}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="flex w-full flex-wrap h-auto justify-start">
          <TabsTrigger value="filters" className="flex items-center space-x-2">
            <Filter className="w-4 h-4" />
            <span>{t('Filters')}</span>
          </TabsTrigger>
          <TabsTrigger value="ip-lists" className="flex items-center space-x-2">
            <Ban className="w-4 h-4" />
            <span>{t('IP lists')}</span>
          </TabsTrigger>
          <TabsTrigger value="activities" className="flex items-center space-x-2">
            <Activity className="w-4 h-4" />
            <span>{t('Suspicious activity')}</span>
          </TabsTrigger>
          <TabsTrigger value="stats" className="flex items-center space-x-2">
            <Eye className="w-4 h-4" />
            <span>{t('Statistics')}</span>
          </TabsTrigger>
        </TabsList>

        {/* Fraud Filters Tab */}
        <TabsContent value="filters" className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">{t('Fraud filters')}</h2>
            <Dialog open={filterDialogOpen} onOpenChange={(open) => { setFilterDialogOpen(open); if (!open) { setEditingItem(null); resetFilterForm(); } }}>
              <DialogTrigger asChild>
                <Button onClick={() => { setEditingItem(null); resetFilterForm(); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('Create filter')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>
                    {editingItem ? t('Edit filter') : t('Create fraud filter')}
                  </DialogTitle>
                  <DialogDescription>
                    {t('Configure automatic filters to block suspicious traffic')}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('Name')}</Label>
                      <Input
                        value={filterForm.name}
                        onChange={(e) => setFilterForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder={t('Filter name')}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('Filter type')}</Label>
                      <Select
                        value={filterForm.filter_type}
                        onValueChange={(value) => setFilterForm(prev => ({ ...prev, filter_type: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bot_detection">{t('Bot detection')}</SelectItem>
                          <SelectItem value="click_frequency">{t('Click frequency')}</SelectItem>
                          <SelectItem value="country_block">{t('Country block')}</SelectItem>
                          <SelectItem value="asn_block">{t('ASN block')}</SelectItem>
                          <SelectItem value="datacenter_block">{t('Datacenter / hosting block')}</SelectItem>
                          <SelectItem value="proxy_vpn_block">{t('Proxy / VPN / Tor block')}</SelectItem>
                          <SelectItem value="external_score">{t('External risk score')}</SelectItem>
                          <SelectItem value="tls_fingerprint">{t('TLS fingerprint (JA3 / JA4)')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('Description')}</Label>
                    <Textarea
                      value={filterForm.description}
                      onChange={(e) => setFilterForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder={t('Filter description')}
                    />
                  </div>

                  {/* Conditions based on filter type */}
                  {filterForm.filter_type === 'click_frequency' && (
                    <div className="space-y-2">
                      <Label>{t('Max clicks per minute')}</Label>
                      <Input
                        type="number"
                        value={filterForm.conditions.max_clicks_per_minute || 10}
                        onChange={(e) => setFilterForm(prev => ({
                          ...prev,
                          conditions: { ...prev.conditions, max_clicks_per_minute: e.target.value }
                        }))}
                        min="1"
                      />
                    </div>
                  )}

                  {filterForm.filter_type === 'country_block' && (
                    <div className="space-y-2">
                      <Label>{t('Blocked countries (ISO codes)')}</Label>
                      <ListInput
                        value={(filterForm.conditions.blocked_countries || [])}
                        onChange={(list) => setFilterForm(prev => ({
                          ...prev,
                          conditions: {
                            ...prev.conditions,
                            blocked_countries: list
                          }
                        }))}
                        placeholder={t('US, GB, DE')}
                      />
                    </div>
                  )}

                  {/* Anti-Fraud Tier 2 filter conditions ------------------- */}
                  {filterForm.filter_type === 'asn_block' && (
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label>{t('ASN numbers')}</Label>
                        <ListInput
                          value={(filterForm.conditions.asns || [])}
                          onChange={(list) => setFilterForm(prev => ({
                            ...prev,
                            conditions: {
                              ...prev.conditions,
                              asns: list
                            }
                          }))}
                          placeholder="16509, 24940, 14061"
                        />
                        <p className="text-xs text-muted-foreground">
                          {t('Comma-separated AS numbers (e.g. 16509 = AWS, 24940 = Hetzner).')}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('ASN name substrings')}</Label>
                        <ListInput
                          value={(filterForm.conditions.asn_names || [])}
                          onChange={(list) => setFilterForm(prev => ({
                            ...prev,
                            conditions: {
                              ...prev.conditions,
                              asn_names: list
                            }
                          }))}
                          placeholder="AMAZON, HETZNER, OVH"
                        />
                        <p className="text-xs text-muted-foreground">
                          {t('Case-insensitive substring match against the ASN label.')}
                        </p>
                      </div>
                    </div>
                  )}

                  {filterForm.filter_type === 'datacenter_block' && (
                    <div className="space-y-2">
                      <Label>{t('Allowed ASNs (whitelist)')}</Label>
                      <ListInput
                        value={(filterForm.conditions.allow_asns || [])}
                        onChange={(list) => setFilterForm(prev => ({
                          ...prev,
                          conditions: {
                            ...prev.conditions,
                            allow_asns: list
                          }
                        }))}
                        placeholder="16509, AMAZON"
                      />
                      <p className="text-xs text-muted-foreground">
                        {t('Optional. Datacenter IPs that match these ASNs / labels are not blocked.')}
                      </p>
                    </div>
                  )}

                  {filterForm.filter_type === 'proxy_vpn_block' && (
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="check_tor"
                          checked={filterForm.conditions.check_tor !== false}
                          onChange={(e) => setFilterForm(prev => ({
                            ...prev,
                            conditions: { ...prev.conditions, check_tor: e.target.checked }
                          }))}
                        />
                        <Label htmlFor="check_tor">{t('Block Tor exit nodes')}</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="check_proxy"
                          checked={!!filterForm.conditions.check_proxy}
                          onChange={(e) => setFilterForm(prev => ({
                            ...prev,
                            conditions: { ...prev.conditions, check_proxy: e.target.checked }
                          }))}
                        />
                        <Label htmlFor="check_proxy">{t('Block proxies (requires API key)')}</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="check_vpn"
                          checked={!!filterForm.conditions.check_vpn}
                          onChange={(e) => setFilterForm(prev => ({
                            ...prev,
                            conditions: { ...prev.conditions, check_vpn: e.target.checked }
                          }))}
                        />
                        <Label htmlFor="check_vpn">{t('Block VPNs (requires API key)')}</Label>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('External provider')}</Label>
                        <Select
                          value={filterForm.conditions.provider || 'ipqs'}
                          onValueChange={(value) => setFilterForm(prev => ({
                            ...prev,
                            conditions: { ...prev.conditions, provider: value }
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ipqs">IPQualityScore</SelectItem>
                            <SelectItem value="fraudscore">FraudScore</SelectItem>
                          </SelectContent>
                        </Select>
                        {providerMissing(filterForm.conditions.provider) && (
                          <SettingWarning testId="proxy-key-missing">
                            {t('There is no key for this provider on the server, so proxy and VPN checks are skipped. Tor blocking works without a key.')}{envHint}
                          </SettingWarning>
                        )}
                      </div>
                    </div>
                  )}

                  {filterForm.filter_type === 'external_score' && (
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label>{t('External provider')}</Label>
                        <Select
                          value={filterForm.conditions.provider || 'ipqs'}
                          onValueChange={(value) => setFilterForm(prev => ({
                            ...prev,
                            conditions: { ...prev.conditions, provider: value }
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ipqs">IPQualityScore</SelectItem>
                            <SelectItem value="fraudscore">FraudScore</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('Risk score threshold (0-100)')}</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={filterForm.conditions.threshold || 85}
                          onChange={(e) => setFilterForm(prev => ({
                            ...prev,
                            conditions: { ...prev.conditions, threshold: e.target.value }
                          }))}
                        />
                        <p className="text-xs text-muted-foreground">
                          {t('Block when the provider returns a score >= this threshold (default 85).')}
                        </p>
                        <SettingWarning testId="score-blocks-now">{t('Clicks are blocked from the moment you save. Start with a high threshold (90 or more) and check the blocked clicks before lowering it.')}</SettingWarning>
                        {providerMissing(filterForm.conditions.provider) && (
                          <SettingWarning testId="score-key-missing">
                            {t('There is no key for this provider on the server, so this filter blocks nothing.')}{envHint}
                          </SettingWarning>
                        )}
                      </div>
                    </div>
                  )}

                  {filterForm.filter_type === 'tls_fingerprint' && (
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label>{t('Block classification')}</Label>
                        <Select
                          value={filterForm.conditions.block_classification || 'bad'}
                          onValueChange={(value) => setFilterForm(prev => ({
                            ...prev,
                            conditions: { ...prev.conditions, block_classification: value }
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="bad">{t('Block fingerprints classified as bad')}</SelectItem>
                            <SelectItem value="">{t('Do not block by classification')}</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          {t('Uses the seed list (Python requests, Go net/http, curl, headless Chrome) plus your workspace overrides.')}
                        </p>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>{t('Block unknown fingerprints')}</Label>
                          <Switch
                            checked={!!filterForm.conditions.block_unknown}
                            onCheckedChange={(checked) => setFilterForm(prev => ({
                              ...prev,
                              conditions: { ...prev.conditions, block_unknown: checked }
                            }))}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {t('Aggressive: blocks any click whose JA3/JA4 is missing or not yet classified. Tag corporate-proxy hashes first via Workspace Overrides.')}
                        </p>
                        {filterForm.conditions.block_unknown && (
                          <SettingWarning testId="block-unknown-on">{t('Real visitors can be blocked too: any browser whose fingerprint is not known yet. Turn this on only after checking which fingerprints your traffic has.')}</SettingWarning>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label>{t('Risk score threshold (0.0 – 1.0)')}</Label>
                        <Input
                          type="number"
                          min="0"
                          max="1"
                          step="0.05"
                          value={filterForm.conditions.risk_threshold ?? 0.7}
                          onChange={(e) => setFilterForm(prev => ({
                            ...prev,
                            conditions: { ...prev.conditions, risk_threshold: e.target.value }
                          }))}
                        />
                        <p className="text-xs text-muted-foreground">
                          {t('Block when the blended TLS+UA+IP risk score exceeds this threshold. Default 0.7.')}
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label>{t('Explicit blocked fingerprints (comma-separated)')}</Label>
                        <Input
                          placeholder="a0e9f5d64349fb13191bc781c3a7c6bf, t13d1517h2_..."
                          value={
                            Array.isArray(filterForm.conditions.blocked_fingerprints)
                              ? filterForm.conditions.blocked_fingerprints.join(', ')
                              : (filterForm.conditions.blocked_fingerprints || '')
                          }
                          onChange={(e) => setFilterForm(prev => ({
                            ...prev,
                            conditions: { ...prev.conditions, blocked_fingerprints: e.target.value }
                          }))}
                        />
                        <p className="text-xs text-muted-foreground">
                          {t('Always block these JA3 / JA4 hashes regardless of classification.')}
                        </p>
                      </div>
                    </div>
                  )}

                  <Separator />

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('Action')}</Label>
                      <Select
                        value={filterForm.action}
                        onValueChange={(value) => setFilterForm(prev => ({ ...prev, action: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="block">{t('Block')}</SelectItem>
                          <SelectItem value="redirect">{t('Redirect')}</SelectItem>
                          <SelectItem value="log">{t('Log only')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {filterForm.action === 'redirect' && (
                      <div className="space-y-2">
                        <Label>{t('Redirect URL')}</Label>
                        <Input
                          value={filterForm.redirect_url}
                          onChange={(e) => setFilterForm(prev => ({ ...prev, redirect_url: e.target.value }))}
                          placeholder="https://example.com"
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outline" onClick={() => setFilterDialogOpen(false)}>
                      {t('Cancel')}
                    </Button>
                    <Button onClick={editingItem ? handleUpdateFilter : handleCreateFilter}>
                      {editingItem ? t('Update') : t('Create')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-4">
            {fraudFilters.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <Filter className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">{t('No active filters')}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('Create filters for automatic fraud protection')}
                  </p>
                </CardContent>
              </Card>
            ) : (
              fraudFilters.map((filter) => (
                <Card key={filter.id} className="border">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <CardTitle className="text-lg">{filter.name}</CardTitle>
                        <Badge variant={filter.is_active ? "default" : "secondary"}>
                          {filter.is_active ? t('Active') : t('Inactive')}
                        </Badge>
                        <Badge variant="outline">
                          {filter.filter_type}
                        </Badge>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openBlockedSamples(filter)}
                          aria-label={t('View blocked samples')}
                          title={t('View blocked samples')}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" aria-label={t('Edit filter')} title={t('Edit filter')} onClick={() => openEditFilter(filter)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" aria-label={t('Delete filter')} title={t('Delete filter')} onClick={() => setDeleteFilterTarget(filter)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {filter.description && (
                      <CardDescription>{filter.description}</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                      <div>
                        <Label className="text-xs text-muted-foreground">{t('Action')}</Label>
                        <p className="capitalize">{filter.action}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">{t('Blocked')}</Label>
                        <p>{filter.total_blocked}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">{t('False positives')}</Label>
                        <p>{filter.false_positives}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* IP Lists Tab */}
        <TabsContent value="ip-lists" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Blacklist */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center space-x-2">
                    <UserX className="w-5 h-5 text-destructive" />
                    <span>{t('IP blacklist')}</span>
                  </CardTitle>
                  <Button size="sm" onClick={() => openAddIP('blacklist')}>
                    <Plus className="w-4 h-4 mr-1" />
                    {t('Add')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {blacklistedIPs.length === 0 ? (
                  <div className="text-center py-8">
                    <Ban className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">{t('Blacklist is empty')}</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {blacklistedIPs.map((ip) => (
                      <div key={ip.id} className="flex items-center justify-between p-3 border rounded">
                        <div>
                          <div className="font-mono text-sm font-medium">{ip.ip_address}</div>
                          <div className="text-xs text-muted-foreground">{ip.reason}</div>
                          {ip.blocked_count > 0 && (
                            <div className="text-xs text-destructive">
                              {t('Blocked: {n} times', { n: ip.blocked_count })}
                            </div>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={t('Remove IP from blacklist')}
                          title={t('Remove IP from blacklist')}
                          onClick={() => handleRemoveIP(ip.id, 'blacklist')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Whitelist */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center space-x-2">
                    <UserCheck className="w-5 h-5 text-primary" />
                    <span>{t('IP whitelist')}</span>
                  </CardTitle>
                  <Button size="sm" onClick={() => openAddIP('whitelist')}>
                    <Plus className="w-4 h-4 mr-1" />
                    {t('Add')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {whitelistedIPs.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">{t('Whitelist is empty')}</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {whitelistedIPs.map((ip) => (
                      <div key={ip.id} className="flex items-center justify-between p-3 border rounded">
                        <div>
                          <div className="font-mono text-sm font-medium">{ip.ip_address}</div>
                          <div className="text-xs text-muted-foreground">{ip.reason}</div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={t('Remove IP from whitelist')}
                          title={t('Remove IP from whitelist')}
                          onClick={() => handleRemoveIP(ip.id, 'whitelist')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* IP Add Dialog */}
          <Dialog open={ipDialogOpen} onOpenChange={(open) => { setIpDialogOpen(open); if (!open) setIpForm({ ip_address: '', reason: '', notes: '' }); }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {ipDialogType === 'blacklist' ? t('Add IP to blacklist') : t('Add IP to whitelist')}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('IP address')}</Label>
                  <Input
                    value={ipForm.ip_address}
                    onChange={(e) => setIpForm(prev => ({ ...prev, ip_address: e.target.value }))}
                    placeholder="192.168.1.1"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('Reason')}</Label>
                  <Input
                    value={ipForm.reason}
                    onChange={(e) => setIpForm(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder={t('Reason for adding')}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('Notes (optional)')}</Label>
                  <Textarea
                    value={ipForm.notes}
                    onChange={(e) => setIpForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder={t('Additional information')}
                  />
                </div>

                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setIpDialogOpen(false)}>
                    {t('Cancel')}
                  </Button>
                  <Button onClick={handleAddIP}>
                    {t('Add')}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Suspicious Activities Tab */}
        <TabsContent value="activities" className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">{t('Suspicious activity')}</h2>
            <Button onClick={fetchAllData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              {t('Refresh')}
            </Button>
          </div>

          <div className="grid gap-4">
            {suspiciousActivities.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">{t('No suspicious activity detected')}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('The system automatically tracks suspicious actions')}
                  </p>
                </CardContent>
              </Card>
            ) : (
              suspiciousActivities.map((activity) => (
                <Card key={activity.id} className="border">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <AlertTriangle className="w-5 h-5 text-orange-500" />
                        <div>
                          <CardTitle className="text-lg capitalize">
                            {activity.activity_type.replace('_', ' ')}
                          </CardTitle>
                          <CardDescription>
                            IP: {activity.ip_address}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant={getSeverityVariant(activity.severity)}>
                          {activity.severity}
                        </Badge>
                        {activity.is_resolved ? (
                          <Badge variant="outline" className="text-primary">
                            {t('Resolved')}
                          </Badge>
                        ) : (
                          <Button size="sm" onClick={() => handleResolveActivity(activity.id)}>
                            {t('Mark as resolved')}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <Label className="text-xs text-muted-foreground">{t('User Agent')}</Label>
                        <p className="font-mono text-xs truncate">{activity.user_agent}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">{t('Time')}</Label>
                        <p>{new Date(activity.created_at).toLocaleString('ru-RU')}</p>
                      </div>
                    </div>

                    {Object.keys(activity.details).length > 0 && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <Label className="text-xs text-muted-foreground">{t('Details')}</Label>
                        <pre className="text-xs bg-surface-2 p-2 rounded mt-1 overflow-x-auto">
                          {JSON.stringify(activity.details, null, 2)}
                        </pre>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Stats Tab */}
        <TabsContent value="stats" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {t('Active filters')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-foreground">
                  {fraudFilters.filter(f => f.is_active).length}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {t('Blocked IPs')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-destructive">
                  {blacklistedIPs.length}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {t('Suspicious activity')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">
                  {suspiciousActivities.filter(a => !a.is_resolved).length}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Wave 25d — bot-detection blocked-samples drill-down. */}
      <Dialog open={samplesDialogOpen} onOpenChange={setSamplesDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {samplesFilter
                ? t('Blocked samples: {name}', { name: samplesFilter.name })
                : t('Blocked samples')}
            </DialogTitle>
            <DialogDescription>
              {t('Most recent traffic caught by this filter. Use it to spot false positives.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {samplesLoading ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                {t('Loading...')}
              </div>
            ) : samplesData.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                {t('No blocked samples yet')}
              </div>
            ) : (
              samplesData.map((sample) => (
                <div key={sample.id} className="border rounded p-3 text-sm">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span>{new Date(sample.created_at).toLocaleString()}</span>
                    <span className="flex items-center space-x-2">
                      {sample.country_code && (
                        <Badge variant="outline">{sample.country_code}</Badge>
                      )}
                      {sample.ip_address && (
                        <span className="font-mono">{sample.ip_address}</span>
                      )}
                    </span>
                  </div>
                  <div className="font-mono text-xs break-all">
                    {sample.user_agent || <em className="text-muted-foreground">{t('(empty UA)')}</em>}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteFilterTarget}
        onOpenChange={(open) => { if (!open) setDeleteFilterTarget(null); }}
        title={t('Delete fraud filter')}
        description={t('This permanently deletes the fraud filter. Traffic it currently blocks or scores will no longer be evaluated by this rule.')}
        confirmLabel={t('Delete')}
        onConfirm={() => { const id = deleteFilterTarget && deleteFilterTarget.id; setDeleteFilterTarget(null); if (id) handleDeleteFilter(id); }}
      />
    </div>
  );
}
